import useAuth from "./hooks/useAuth";
import { useEffect, useState } from "react";
import PhonemeChart from "./PhonemeChart";

const UserPhonemesSnapshot = ({ language, date }) => {
    const { auth, setAuth } = useAuth();
    const [stats, setStats] = useState();
    const [loading, setLoading] = useState(false);

    useEffect(() => { 
        if (language) {
            getMyStats(); 
        }
    }, [language, date]);

    const getMyStats = async () => {
        const token = localStorage.getItem("token");
        setLoading(true);
        
        try {
            let url = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/phonemes`;
            
            if (date !== 'Select date' && date !== 'All records') {
                url += `?date=${date}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const json = await response.json();
                setStats(json);
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error fetching phoneme stats:", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Loading phoneme statistics...</p>
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-2">📊</div>
                <p className="text-gray-500">No phoneme data available</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-full space-y-8">
            {/* Header */}
            <div className="text-center">
                <h2 className="text-2xl font-bold text-blue-700">Phoneme Performance Snapshot</h2>
                <p className="text-gray-600 mt-2">
                    Overview of your pronunciation accuracy across all phonemes
                </p>
            </div>

            {/* Stats Summary */}
            {stats.phonemes && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-2xl font-bold text-blue-600">{stats.phonemes.length}</div>
                            <div className="text-sm text-gray-600">Total Phonemes</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-2xl font-bold text-green-600">
                                {Object.values(stats.colored).reduce((sum, item) => sum + (item.green || 0), 0)}
                            </div>
                            <div className="text-sm text-gray-600">High Accuracy</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-2xl font-bold text-yellow-600">
                                {Object.values(stats.colored).reduce((sum, item) => sum + (item.yellow || 0), 0)}
                            </div>
                            <div className="text-sm text-gray-600">Medium Accuracy</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-2xl font-bold text-red-600">
                                {Object.values(stats.colored).reduce((sum, item) => sum + (item.red || 0), 0)}
                            </div>
                            <div className="text-sm text-gray-600">Needs Practice</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Phoneme Charts Grid - Fixed container constraints */}
            {stats.phonemes && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                    {stats.phonemes.map((key, i) => {
                        const data = [
                            { name: "80-100%", value: stats.colored[key]?.green || 0, color: "#10B981" },
                            { name: "40-80%", value: stats.colored[key]?.yellow || 0, color: "#F59E0B" },
                            { name: "0-40%", value: stats.colored[key]?.red || 0, color: "#EF4444" }
                        ];
                        
                        const total = stats.colored[key]?.total || 0;
                        const sample = stats.colored[key]?.sample || "";

                        // Position-specific data
                        let data_start = null;
                        let data_end = null;
                        let data_inside = null;
                        let total_start = 0;
                        let total_end = 0;
                        let total_inside = 0;
                        let sample_start = "";
                        let sample_end = "";
                        let sample_inside = "";

                        if (stats.colored.hasOwnProperty(key + "_start")) {
                            data_start = [
                                { name: "80-100%", value: stats.colored[key + '_start']?.green || 0, color: "#10B981" },
                                { name: "40-80%", value: stats.colored[key + '_start']?.yellow || 0, color: "#F59E0B" },
                                { name: "0-40%", value: stats.colored[key + '_start']?.red || 0, color: "#EF4444" }
                            ];
                            total_start = stats.colored[key + '_start']?.total || 0;
                            sample_start = stats.colored[key + '_start']?.sample || "";
                        }

                        if (stats.colored.hasOwnProperty(key + "_inside")) {
                            data_inside = [
                                { name: "80-100%", value: stats.colored[key + '_inside']?.green || 0, color: "#10B981" },
                                { name: "40-80%", value: stats.colored[key + '_inside']?.yellow || 0, color: "#F59E0B" },
                                { name: "0-40%", value: stats.colored[key + '_inside']?.red || 0, color: "#EF4444" }
                            ];
                            total_inside = stats.colored[key + '_inside']?.total || 0;
                            sample_inside = stats.colored[key + '_inside']?.sample || "";
                        }

                        if (stats.colored.hasOwnProperty(key + "_end")) {
                            data_end = [
                                { name: "80-100%", value: stats.colored[key + '_end']?.green || 0, color: "#10B981" },
                                { name: "40-80%", value: stats.colored[key + '_end']?.yellow || 0, color: "#F59E0B" },
                                { name: "0-40%", value: stats.colored[key + '_end']?.red || 0, color: "#EF4444" }
                            ];
                            total_end = stats.colored[key + '_end']?.total || 0;
                            sample_end = stats.colored[key + '_end']?.sample || "";
                        }

                        return (
                            <div 
                                key={`${key}-${i}-${language}`} 
                                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all duration-200 hover:border-blue-300 overflow-hidden min-h-[280px] flex flex-col"
                            >
                                <div className="flex-1 flex flex-col min-w-0"> {/* Constrain content */}
                                    <PhonemeChart 
                                        data={data}
                                        data_start={data_start}
                                        data_end={data_end}
                                        data_inside={data_inside}
                                        count={total}
                                        count_start={total_start}
                                        count_end={total_end}
                                        count_inside={total_inside}
                                        phoneme={key}
                                        sample={sample}
                                        sample_start={sample_start}
                                        sample_end={sample_end}
                                        sample_inside={sample_inside}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Legend */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Accuracy Legend</h3>
                <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-gray-600">80-100% (Excellent)</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <span className="text-gray-600">40-80% (Good)</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span className="text-gray-600">0-40% (Needs Practice)</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserPhonemesSnapshot;